import type { ReactElement, ReactNode } from 'react';
import { Button } from '../primitives/index.js';
import { ApiError } from '../../lib/api/errors.js';

export interface EmptyStateProps {
  title: string;
  /** One sentence. */
  body?: string;
  action?: ReactNode;
}

/**
 * Distinguishes "nothing yet" from "nothing matched" by the copy the caller
 * supplies. No illustration — this is an operational tool.
 */
export function EmptyState({ title, body, action }: EmptyStateProps): ReactElement {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      {body === undefined ? null : <p className="state__body">{body}</p>}
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
}

/**
 * Renders problem+json as the backend sends it. `retryable` comes from the
 * response, so the retry affordance is DERIVED rather than guessed.
 *
 * §4.4 — a 404 may mean "not yours" as easily as "gone". Both render the same
 * sentence, or the UI re-opens the leak the API closed.
 */
export function ErrorState({ error, onRetry }: ErrorStateProps): ReactElement {
  if (error instanceof ApiError && error.isNotFoundOrForbidden) {
    return (
      <div className="state">
        <p className="state__title">Not available</p>
        <p className="state__body">
          This isn’t available to you. It may not exist, or it may belong to someone else.
        </p>
      </div>
    );
  }

  const title = error instanceof ApiError ? error.title : 'Something went wrong';
  const detail =
    error instanceof ApiError && error.detail !== ''
      ? error.detail
      : 'The request could not be completed.';
  const code = error instanceof ApiError ? error.code : undefined;
  const canRetry = error instanceof ApiError ? error.retryable : true;

  return (
    <div className="state" role="alert">
      <p className="state__title">{title}</p>
      <p className="state__body">{detail}</p>
      {code === undefined ? null : <p className="state__code">{code}</p>}
      {canRetry && onRetry ? (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export interface BlockedStateProps {
  /** What the user came here to do. */
  capability: string;
  /** Plain-language reason, never a ticket number. */
  reason?: string;
  /**
   * Rail density: left-aligned, no dashed box, a hairline rule down the side.
   *
   * The full treatment is a centred, dashed panel sized for a page whose whole
   * subject is the missing capability. Inside a 260px filter rail the SAME
   * component became the largest element on the search page — a 400px block of
   * caveat with more visual weight than the machines it was a caveat about.
   *
   * This changes the density and nothing else. It is still `BlockedState` and
   * not a `Note`, because the distinction is load-bearing: distance search is a
   * capability that does not exist, and it must not read as a control that
   * happens to be switched off.
   */
  compact?: boolean;
}

/**
 * The component the 58 blocked endpoints render.
 *
 * It states plainly that the capability is not available. There is deliberately
 * NO disabled button and NO "coming soon" — a disabled affordance implies the
 * feature exists and is merely switched off, which would be untrue.
 */
export function BlockedState({
  capability,
  reason,
  compact = false,
}: BlockedStateProps): ReactElement {
  return (
    <div className={`state state--blocked${compact ? ' state--compact' : ''}`}>
      <p className="state__title">{capability} isn’t available yet</p>
      <p className="state__body">
        {reason ??
          'This part of the product is not built. It will appear here once it is, and nothing is hidden from you in the meantime.'}
      </p>
    </div>
  );
}
